import Component from '@glimmer/component';
import { assert } from '@ember/debug';
import { hash } from '@ember/helper';
import { LinkTo } from '@ember/routing';
import { PhCaretRight } from 'ember-phosphor-icons';
import Heading from './heading';
import Text from './text';
import type { TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';
import type { ComponentLike } from '@glint/template';
import type Owner from '@ember/owner';

// ---------------------------------------------------------------------------
// PageHeader – consistent page-level header with breadcrumbs & actions
// ---------------------------------------------------------------------------

// --- Crumb sub-component ------------------------------------------------

interface CrumbSignature {
  Args: {
    /** Display label */
    label: string;
    /** Route name for navigation (omit for current/terminal crumb) */
    route?: string;
    /** Route model for dynamic segments */
    model?: unknown;
  };
  Element: HTMLLIElement;
}

/**
 * Breadcrumb crumb — renders a link or static label.
 * The trailing caret separator auto-hides on the last crumb via `last:hidden`.
 */
const Crumb: TOC<CrumbSignature> = <template>
  <li class="tio-inline-row-extra-tight [&:last-of-type>.crumb-sep]:hidden" ...attributes>
    {{#if @route}}
      {{#if @model}}
        <LinkTo
          @route={{@route}}
          @model={{@model}}
          class="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {{@label}}
        </LinkTo>
      {{else}}
        <LinkTo
          @route={{@route}}
          class="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          {{@label}}
        </LinkTo>
      {{/if}}
    {{else}}
      <span class="font-medium text-[var(--text-primary)]">{{@label}}</span>
    {{/if}}
    <PhCaretRight
      @size={{14}}
      class="crumb-sep ml-1 text-[var(--text-disabled)] shrink-0"
      aria-hidden="true"
    />
  </li>
</template>;

// --- PageHeader ---------------------------------------------------------

interface PageHeaderSignature {
  Args: {
    /** Page title — rendered as the page's visible semantic h1. */
    title?: string;
    /** Accessible title for screen readers — used as sr-only h1 when @title is omitted (compact breadcrumb mode). Required when @title is not provided. */
    a11yTitle?: string;
    /** Optional subtitle below the title or breadcrumbs */
    subtitle?: string;
    /** Optional Phosphor icon component displayed before the title */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  };
  Blocks: {
    /** Breadcrumb navigation — yields `{ crumb }` for building a breadcrumb trail */
    breadcrumbs?: [{ crumb: WithBoundArgs<typeof Crumb, 'model'> }];
    /** Center toolbar area between title and actions (search, filters, etc.) */
    toolbar?: [];
    /** Right-side actions area (buttons, tools, dropdowns) */
    actions?: [];
    /** Content below the title bar (tabs, filters, search, etc.) */
    below?: [];
  };
  Element: HTMLElement;
}

/**
 * PageHeader – a consistent page-level header that enforces one semantic h1
 * per page, with optional breadcrumbs, a right-side actions area, and a
 * below-the-title slot for tabs or filters.
 *
 * Place at the top of every route template to establish heading hierarchy
 * and provide a standard location for navigation and contextual actions.
 *
 * @example Basic — just a title
 * ```gts
 * import PageHeader from 'tio-ui/components/page-header';
 *
 * <PageHeader @title="People" />
 * ```
 *
 * @example With breadcrumbs and actions
 * ```gts
 * import PageHeader from 'tio-ui/components/page-header';
 * import Button from './button';
 *
 * <PageHeader @title="Email Templates" @subtitle="Manage notification templates">
 *   <:breadcrumbs as |b|>
 *     <b.crumb @route="authenticated.settings" @label="Settings" />
 *     <b.crumb @label="Email Templates" />
 *   </:breadcrumbs>
 *   <:actions>
 *     <Button @intent="primary">Create Template</Button>
 *   </:actions>
 * </PageHeader>
 * ```
 *
 * @example With icon and below content
 * ```gts
 * import PageHeader from 'tio-ui/components/page-header';
 * import { PhUsers } from 'ember-phosphor-icons';
 *
 * <PageHeader @title="People" @icon={{PhUsers}}>
 *   <:below>
 *     <SearchInput @placeholder="Search people..." />
 *   </:below>
 * </PageHeader>
 * ```
 *
 * @example Breadcrumbs with dynamic model
 * ```gts
 * <PageHeader @title={{this.company.name}}>
 *   <:breadcrumbs as |b|>
 *     <b.crumb @route="authenticated.companies" @label="Companies" />
 *     <b.crumb @route="authenticated.companies.show" @model={{this.company}} @label={{this.company.name}} />
 *     <b.crumb @label="Settings" />
 *   </:breadcrumbs>
 * </PageHeader>
 * ```
 */
export default class PageHeader extends Component<PageHeaderSignature> {
  constructor(owner: Owner, args: PageHeaderSignature['Args']) {
    super(owner, args);
    assert(
      '<PageHeader> requires either @title or @a11yTitle to ensure every page has a semantic h1 for accessibility.',
      args.title || args.a11yTitle
    );
  }

  <template>
    <header data-test-page-header class="app-page-header px-6 py-3" ...attributes>
      {{#if @title}}
        {{! === Mode 1: Title present — breadcrumbs above, title row below === }}
        {{#if (has-block "breadcrumbs")}}
          <nav data-test-page-header-breadcrumbs aria-label="Breadcrumbs" class="mb-1">
            <ol class="tio-row-flush text-sm">
              {{yield (hash crumb=(component Crumb)) to="breadcrumbs"}}
            </ol>
          </nav>
        {{/if}}

        <div data-test-page-header-title-row class="flex flex-wrap items-center gap-4">
          <div data-test-page-header-title-group class="tio-row min-w-0">
            {{#if @icon}}
              {{#let @icon as |Icon|}}
                <Icon @size={{24}} class="text-[var(--text-tertiary)] shrink-0" />
              {{/let}}
            {{/if}}
            <div class="min-w-0">
              <Heading data-test-page-header-title @level={{1}} @size="xl">
                {{@title}}
              </Heading>
              {{#if @subtitle}}
                <Text data-test-page-header-subtitle @variant="muted" @size="sm" class="mt-0.5">
                  {{@subtitle}}
                </Text>
              {{/if}}
            </div>
          </div>

          {{#if (has-block "toolbar")}}
            <div
              data-test-page-header-toolbar
              class="tio-row-flush flex-1 justify-end min-w-0 px-4"
            >
              <div class="w-full max-w-md">
                {{yield to="toolbar"}}
              </div>
            </div>
          {{/if}}

          {{#if (has-block "actions")}}
            <div data-test-page-header-actions class="tio-row-tight ml-auto shrink-0">
              {{yield to="actions"}}
              <div id="page-header-actions-portal" class="contents"></div>
            </div>
          {{else}}
            <div class="tio-row-tight ml-auto shrink-0">
              <div id="page-header-actions-portal" class="contents"></div>
            </div>
          {{/if}}
        </div>
      {{else}}
        {{! === Mode 2: No title — compact breadcrumb-only header === }}
        {{#if @a11yTitle}}
          <Heading data-test-page-header-title @level={{1}} class="sr-only">
            {{@a11yTitle}}
          </Heading>
        {{/if}}
        <div class="flex flex-wrap items-center gap-4">
          {{#if (has-block "breadcrumbs")}}
            <nav data-test-page-header-breadcrumbs aria-label="Breadcrumbs" class="min-w-0 flex-1">
              <ol class="tio-row-flush text-sm">
                {{yield (hash crumb=(component Crumb)) to="breadcrumbs"}}
              </ol>
              {{#if @subtitle}}
                <Text data-test-page-header-subtitle @variant="muted" @size="xs">
                  {{@subtitle}}
                </Text>
              {{/if}}
            </nav>
          {{/if}}

          {{#if (has-block "toolbar")}}
            <div
              data-test-page-header-toolbar
              class="tio-row-flush flex-1 justify-end min-w-0 px-4"
            >
              <div class="w-full max-w-md">
                {{yield to="toolbar"}}
              </div>
            </div>
          {{/if}}

          {{#if (has-block "actions")}}
            <div data-test-page-header-actions class="tio-row-tight shrink-0 ml-auto">
              {{yield to="actions"}}
              <div id="page-header-actions-portal" class="contents"></div>
            </div>
          {{else}}
            <div class="tio-row-tight shrink-0 ml-auto">
              <div id="page-header-actions-portal" class="contents"></div>
            </div>
          {{/if}}
        </div>
      {{/if}}

      {{! Below area (tabs, filters, etc.) — full-bleed so a PageControlBar spans
          edge-to-edge and its content aligns with the title (negate the header's
          px-6 / bottom py-3). }}
      {{#if (has-block "below")}}
        <div data-test-page-header-below class="app-page-header__below">
          {{yield to="below"}}
        </div>
      {{/if}}
    </header>
  </template>
}

export { Crumb as PageHeaderCrumb };
export type { PageHeaderSignature, CrumbSignature as PageHeaderCrumbSignature };
